import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { getStory, STORIES } from "@/content/stories";
import { setStoryProgress } from "@/lib/progress";
import { printSheet } from "@/lib/print";
import Icon from "@/components/shared/Icon";
import Seo from "@/components/shared/Seo";

export default function StoryDetail() {
  const { t, lp, code } = useLang();
  const { slug } = useParams();
  const story = getStory(slug);
  const [page, setPage] = useState(0);

  if (!story) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <p className="text-lg font-semibold text-[#475569]">{t("stories.notFound")}</p>
        <Link to={lp("/stories")} className="mt-4 inline-block font-bold text-[#147DEB] hover:underline">← {t("nav.stories")}</Link>
      </div>
    );
  }

  const total = story.pages.length;
  const pct = Math.round(((page + 1) / total) * 100);
  const atEnd = page === total - 1;

  const go = (dir) => {
    const next = Math.min(Math.max(page + dir, 0), total - 1);
    setPage(next);
    setStoryProgress(slug, Math.round(((next + 1) / total) * 100));
  };

  const related = STORIES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <>
      <Seo title={story.title[code]} description={story.desc[code]} />

      <div className="border-b border-[#147DEB]/10 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to={lp("/stories")} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#147DEB] hover:underline" data-testid="story-back-link">
            <Icon name="ArrowLeft" className="h-4 w-4" /> {t("nav.stories")}
          </Link>
          <button onClick={() => printSheet(story.title[code], story.activity[code], code)} data-testid="story-print-button" className="inline-flex items-center gap-1.5 rounded-xl bg-[#E8F5FF] px-3.5 py-2 text-sm font-bold text-[#147DEB] hover:bg-[#D4EDFF]">
            <Icon name="Printer" className="h-4 w-4" /> {t("stories.printSheet")}
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <h1 className="font-display text-center text-3xl font-extrabold tracking-tight text-[#082B66] sm:text-4xl">{story.title[code]}</h1>

        <div className="mt-6">
          <div className="flex items-center justify-between text-xs font-semibold text-[#64748B]">
            <span>{t("stories.readingProgress")}</span>
            <span>{t("stories.page")} {page + 1} / {total}</span>
          </div>
          <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-[#E8F5FF]">
            <div className="h-full rounded-full bg-[#45C878] transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={page}
            initial={{ opacity: 0, rotateY: -8, x: 20 }}
            animate={{ opacity: 1, rotateY: 0, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.35 }}
            className="mt-6 rounded-3xl p-10 text-center card-shadow-soft"
            style={{ backgroundColor: `${story.cover}20`, border: `2px solid ${story.cover}55` }}
            data-testid="story-page"
          >
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl" style={{ backgroundColor: story.cover }}>
              <Icon name="BookOpen" className="h-8 w-8 text-white" />
            </div>
            <p className="font-display text-2xl leading-relaxed text-[#082B66] sm:text-3xl">{story.pages[page][code]}</p>
          </motion.div>
        </AnimatePresence>

        {atEnd && (
          <div className="mt-6 rounded-2xl border-2 border-[#FFC928]/40 bg-[#FFF9E6] p-5 text-center" data-testid="story-activity">
            <p className="font-semibold text-[#082B66]">{story.activity[code]}</p>
          </div>
        )}

        <div className="mt-8 flex items-center justify-between gap-3">
          <button onClick={() => go(-1)} disabled={page === 0} data-testid="story-prev-button" className="inline-flex items-center gap-1.5 rounded-2xl border-2 border-[#147DEB]/15 bg-white px-5 py-3 text-sm font-bold text-[#082B66] hover:border-[#147DEB] disabled:opacity-40">
            <Icon name="ArrowLeft" className="h-4 w-4" /> {t("common.previous")}
          </button>
          {atEnd ? (
            <Link to={lp("/stories")} data-testid="story-finish-button" className="inline-flex items-center gap-1.5 rounded-2xl bg-[#45C878] px-6 py-3 text-sm font-bold text-white hover:bg-[#34B566]">
              <Icon name="Check" className="h-4 w-4" /> {t("stories.finish")}
            </Link>
          ) : (
            <button onClick={() => go(1)} data-testid="story-next-button" className="inline-flex items-center gap-1.5 rounded-2xl bg-[#147DEB] px-6 py-3 text-sm font-bold text-white hover:bg-[#0F62BD]">
              {t("common.next")} <Icon name="ArrowRight" className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="mt-16">
          <h3 className="font-display text-xl font-bold text-[#082B66]">{t("lesson.related")}</h3>
          <div className="mt-5 grid gap-4 sm:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to={lp(`/stories/${r.slug}`)} onClick={() => setPage(0)} className="rounded-2xl border-2 border-[#147DEB]/10 bg-white p-5 transition-all hover:-translate-y-1 hover:shadow-lg" data-testid={`related-story-${r.slug}`}>
                <h4 className="font-display font-bold text-[#082B66]">{r.title[code]}</h4>
                <p className="mt-1 text-sm text-[#64748B]">{r.minutes} {t("common.min")}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
